import getDistance3D from '../utils/math/getDistance3D';
import { crossProduct, innerProduct } from '../utils/math';

const DEFAULT_SINGLE_SLICE_THICKNESS = 0; // mm

function hasValidIPP(slice){
  const ipp = slice.ImagePositionPatient;
  return (
    ipp && 
    (ipp[0] || ipp[0] === 0) &&
    (ipp[1] || ipp[1] === 0) &&
    (ipp[2] || ipp[2] === 0)
  ); // return boolean
}

function getScanAxisNormal(slice){
  const iop = slice?.ImageOrientationPatient;
  if (!iop) {
    return null;
  }
  return crossProduct(
    [iop[0], iop[1], iop[2]],
    [iop[3], iop[4], iop[5]]
  );
}

function projectOntoNormal(slice, axisNormal){
  const ipp = slice?.ImagePositionPatient;
  return innerProduct(ipp, axisNormal);
}

function ippDistance(a, b) {
  return getDistance3D({ x: a[0], y: a[1], z: a[2] }, { x: b[0], y: b[1], z: b[2] });
}

function getResampleAxisSpacing(stack){
  const stackWithIPP = stack.filter(hasValidIPP);
  if (stackWithIPP.length <= 1) return null;

  let span = 0;

  const axisNormal = getScanAxisNormal(stackWithIPP[0]);
  if (axisNormal) {
    // AxisProjectsionResampleSpacing
    let max = -Infinity;
    let min = Infinity;
    for (const slice of stackWithIPP){
      const projection = projectOntoNormal(slice, axisNormal);
      if (projection === null) continue;
      max = Math.max(max, projection);
      min = Math.min(min, projection);
    } 
    if ((max === -Infinity) || (min === Infinity)) return null;
    span = max-min;
  } else {
    // fallback: orientation-free farthest-point
    let farSlice = stackWithIPP[0];
    let maxDist = -Infinity;
    for (const slice of stackWithIPP){
      const refSliceIPP = stackWithIPP[0].ImagePositionPatient;
      const curSliceIPP = slice.ImagePositionPatient;
      const d = ippDistance(refSliceIPP, curSliceIPP);
      if (d > maxDist){
        maxDist = d;
        farSlice = slice;
      }
    }
    for (const slice of stackWithIPP){
      const refSliceIPP = farSlice.ImagePositionPatient;
      const curSliceIPP = slice.ImagePositionPatient;
      const d = ippDistance(refSliceIPP, curSliceIPP);
      if (d >= span){
        span = d;
      }
    }
  }
  return span /(stackWithIPP.length - 1);
}

function getNearestNeighborSpacing(singleSlice, initialStack){
  const refWithIPP = initialStack.filter(
    (slice) => slice !== singleSlice && hasValidIPP(slice));
  if (refWithIPP.length < 1) return null;

  let nearestDist = Infinity;
  for (const slice of refWithIPP){
    const singleIPP= singleSlice.ImagePositionPatient;
    const curIPP = slice.ImagePositionPatient;
    const d = ippDistance(singleIPP, curIPP);
    if ( d > 0 &&  d < nearestDist) {
      nearestDist = d;
    }
  }
  return Number.isFinite(nearestDist) ? nearestDist : null;
}

const firstPositive = (...vals) =>
  vals.find(v => typeof(v) === 'number' && v > 0);

function integrateSlicesResample(stack, initialStack){
  const axisSpacing = (
    getResampleAxisSpacing(stack) ??  // if 0 fallback edgecase handle
    getNearestNeighborSpacing(stack[0], initialStack) ??
    firstPositive(
      ...stack.map(s => s.SpacingBetweenSlices),
      ...stack.map(s => s.SliceThickness)
    ) ??
    DEFAULT_SINGLE_SLICE_THICKNESS // default
    );
  return stack.reduce((acc, cur) => {
    acc += (cur.area || 0) * axisSpacing;
    return acc;
  }, 0);
}

function getVolumeFromSlices(slices) {
  let volume = 0;
  const initialStack = slices;
  const acqNumStackGroups = initialStack.reduce((groups, slice) => {
    const acqNum = slice.AcquisitionNumber;
    if (!groups[acqNum]){
      groups[acqNum] = []
    }
    groups[acqNum].push(slice);
    return groups;
  }, {})
  for (const stack of Object.values(acqNumStackGroups)){
    volume += integrateSlicesResample(stack, initialStack);
  }
  return volume;
}

export default getVolumeFromSlices;
